/** Lab API client — authenticated own-ledger historical analysis. */

export type LabOutcome = "all" | "assigned" | "expired" | "closed" | "open" | "unresolved";

export interface LabFilters {
  side?: "put" | "call" | "all";
  outcome?: LabOutcome;
  ticker?: string;
  accountId?: string;
  from?: string;
  to?: string;
  popMin?: number | null;
  popMax?: number | null;
  popQuality?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  dir?: "asc" | "desc";
  targetPop?: number;
}

export interface LabContract {
  id: number;
  ticker: string;
  side: "put" | "call" | null;
  strike: number | null;
  expDate: string | null;
  openDate: string | null;
  resolutionDate: string | null;
  contracts: number;
  premium: number;
  outcome: string;
  openSpot: number | null;
  assignSpot: number | null;
  priceChangePct: number | null;
  openPop: number | null;
  openPopPct: number | null;
  openPopQuality: string;
  openPopLabel: string;
  openPopTip?: string;
  premiumAdjustedCost: number | null;
  expirationPayoff: number | null;
}

export interface LabSummary {
  soldTrades: number;
  contractCount: number;
  resolvedCount: number;
  assignedCount: number;
  assignmentRate: number | null;
  assignmentRateLabel: string;
  averageOpenPopPct: number | null;
  premiumCollected: number;
  premiumNote: string;
  downsideImpact: number;
  downsideNote: string;
  coverage: {
    withRecordedPop: number;
    missingPop: number;
    withOpenSpot: number;
    withAssignSpot: number;
    sampleDenominator: string;
  };
}

export interface LabScenario {
  targetPop: number;
  targetPopPct: number;
  available: boolean;
  strike: number | null;
  optionSymbol: string | null;
  premiumEstimate: number | null;
  premiumConvention: string | null;
  estimatedPop: number | null;
  estimatedPopPct: number | null;
  expirationPayoff: number | null;
  vsActualPremium: number | null;
  vsActualPayoff: number | null;
  provenance: string | null;
  unavailableReason: string | null;
  modelVersion: string | null;
}

export interface LabStrategyComparison {
  available: boolean;
  reason: string | null;
  targetPop: number;
  targetPopPct: number;
  actual: LabSummary;
  eligibleCount: number;
  excludedCount: number;
  exclusions: { byReason: Record<string, number>; note: string };
  hypothetical: {
    premiumCollected: number;
    modeledExpirationPayoff: number;
    assignmentRate: number | null;
    premiumGivenUp: number;
    payoffDelta: number;
  } | null;
  caveats: string[];
}

function qs(filters: LabFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value == null || value === "" || value === "all") continue;
    params.set(key, String(value));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function labGet<T>(path: string): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message =
      (body as { detail?: { message?: string } })?.detail?.message ||
      `Lab request failed (${res.status})`;
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export async function fetchLabFilters(): Promise<{ tickers: string[]; accounts: string[] }> {
  const body = await labGet<{ ok: boolean; tickers: string[]; accounts: string[] }>(
    "/api/lab/filters",
  );
  return { tickers: body.tickers ?? [], accounts: body.accounts ?? [] };
}

export async function fetchLabSummary(
  filters: LabFilters,
): Promise<{ summary: LabSummary; filters: LabFilters }> {
  const body = await labGet<{ ok: boolean; summary: LabSummary; filters: LabFilters }>(
    `/api/lab/summary${qs(filters)}`,
  );
  return { summary: body.summary, filters: body.filters };
}

export async function fetchLabContracts(
  filters: LabFilters,
): Promise<{ contracts: LabContract[]; total: number }> {
  const body = await labGet<{
    ok: boolean;
    contracts: LabContract[];
    total: number;
  }>(`/api/lab/contracts${qs(filters)}`);
  return { contracts: body.contracts ?? [], total: body.total ?? 0 };
}

export async function fetchLabContract(id: number): Promise<LabContract> {
  const body = await labGet<{ ok: boolean; contract: LabContract }>(`/api/lab/contracts/${id}`);
  return body.contract;
}

export async function fetchLabContractPrices(
  id: number,
  opts?: { before?: number; after?: number },
): Promise<{
  candles: { date: string; close: number }[];
  strike: number | null;
  openDate: string | null;
  resolutionDate: string | null;
  openSpot: number | null;
  assignSpot: number | null;
  subsequent: Record<string, number | null>;
  unavailable: boolean;
}> {
  const params = new URLSearchParams();
  if (opts?.before != null) params.set("before", String(opts.before));
  if (opts?.after != null) params.set("after", String(opts.after));
  const q = params.toString();
  return labGet(`/api/lab/contracts/${id}/prices${q ? `?${q}` : ""}`);
}

export async function fetchLabScenarios(id: number): Promise<{
  available: boolean;
  reason: string | null;
  scenarios: LabScenario[];
  caveats?: string[];
  cached?: boolean;
}> {
  return labGet(`/api/lab/contracts/${id}/scenarios`);
}

export async function fetchLabStrategyComparison(
  filters: LabFilters,
): Promise<LabStrategyComparison> {
  return labGet(`/api/lab/strategy-comparison${qs({ ...filters, targetPop: filters.targetPop ?? 0.85 })}`);
}
